/**
 * AI Scribe V2 — generated note card
 *
 * The standardised note surface: one centred document card, a title rule, then
 * each section as a small uppercase label over its body copy.
 *
 * Presentational only — it receives an already-flattened note.
 *
 * @author [Rajkumar Rathod]
 */

import { Box, SxProps, Theme } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import { NoteSectionView } from "../../ai-scriber-utils";
import { SCRIBE_V2_COLORS, SCRIBE_V2_DIMENSIONS } from "../constants";

export interface ScribeNoteCardProps {
  title: string;
  sections: NoteSectionView[];
  sx?: SxProps<Theme>;
}

const ScribeNoteCard = ({ title, sections, sx }: ScribeNoteCardProps) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      width: "100%",
      overflowY: "auto",
      backgroundColor: SCRIBE_V2_COLORS.noteCanvasBg,
      px: px(PRIMITIVE.spacing[16]),
      py: px(PRIMITIVE.spacing[16]),
      ...(sx as object),
    }}
  >
    <Flex
      flexDirection="column"
      gap={px(PRIMITIVE.spacing[16])}
      sx={{
        mx: "auto",
        width: "100%",
        maxWidth: px(SCRIBE_V2_DIMENSIONS.noteCardMaxWidth),
        p: px(PRIMITIVE.spacing[24]),
        backgroundColor: SCRIBE_V2_COLORS.windowBg,
        border: `1px solid ${SCRIBE_V2_COLORS.noteCardBorder}`,
        borderRadius: SCRIBE_V2_DIMENSIONS.noteCardRadius,
      }}
    >
      <Box
        sx={{
          pb: px(PRIMITIVE.spacing[12]),
          borderBottom: `1px solid ${SCRIBE_V2_COLORS.noteCardBorder}`,
        }}
      >
        <Text
          component="h2"
          sx={{
            m: 0,
            color: SCRIBE_V2_COLORS.textPrimary,
            fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
            fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
          }}
        >
          {title}
        </Text>
      </Box>

      {sections.map((section) => (
        <Flex key={section.key} flexDirection="column" gap={0.5}>
          <Text
            sx={{
              color: SCRIBE_V2_COLORS.noteSectionLabel,
              fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
              fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
              textTransform: "uppercase",
              letterSpacing: SCRIBE_V2_DIMENSIONS.noteLabelTracking,
            }}
          >
            {section.label}
          </Text>

          {section.paragraphs.map((paragraph, index) => (
            <Text
              key={`${section.key}-${paragraph.label ?? index}`}
              sx={{
                color: SCRIBE_V2_COLORS.textPrimary,
                fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
                lineHeight: SCRIBE_V2_DIMENSIONS.noteLineHeight,
              }}
            >
              {paragraph.label ? (
                <Box
                  component="span"
                  sx={{
                    fontWeight: PRIMITIVE.typography.fontWeight.medium,
                    color: SCRIBE_V2_COLORS.textSecondary,
                  }}
                >
                  {`${paragraph.label}: `}
                </Box>
              ) : null}
              {paragraph.text}
            </Text>
          ))}
        </Flex>
      ))}
    </Flex>
  </Box>
);

export default ScribeNoteCard;
